/**
 * Le Parc — poste de travail unique (remplace Tiers/Sites/Machines/Import).
 *
 * Recherche transversale, client en combo filtrable, sites en chips, bandeau
 * de statistiques par nature, filtres secondaires, liste et fiche machine
 * côte à côte : sélectionner une machine ne change JAMAIS de page, seul l'état
 * de sélection change — porté par les paramètres d'URL pour rester
 * copiable/partageable (même principe déjà utilisé par `EtiquettesQrPage`).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, api, type Asset, type AssetFacets } from '../api/client';
import { ChipsSecondaires, type ChipParc } from '../composants/ChipsSecondaires';
import { ChipsSite } from '../composants/ChipsSite';
import { ComboRecherche } from '../composants/ComboRecherche';
import { FicheMachine } from '../composants/FicheMachine';
import { FormulaireMachine } from '../composants/FormulaireMachine';
import { FormulaireSite } from '../composants/FormulaireSite';
import { FormulaireTiers } from '../composants/FormulaireTiers';
import { StatsNature } from '../composants/StatsNature';
import { STATUT_MACHINE } from '../domaine/machines';
import { lienMachinesAValider } from '../domaine/machines-a-valider';

const TAILLE_PAGE = 100;

export function ParcPage() {
  const [params, setParams] = useSearchParams();

  const q = params.get('q') ?? '';
  const client = params.get('client');
  const site = params.get('site');
  const nature = params.get('nature');
  const chip = params.get('chip') as ChipParc | null;
  const asset = params.get('asset');

  const [recherche, setRecherche] = useState(q);
  const [items, setItems] = useState<Asset[]>([]);
  const [total, setTotal] = useState(0);
  const [take, setTake] = useState(TAILLE_PAGE);
  const [facets, setFacets] = useState<AssetFacets | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  // `true` dès le premier rendu : le point de vérification de la sélection (plus
  // bas) attend la fin du PREMIER chargement. Avec `false`, il s'exécutait sur une
  // liste encore vide et effaçait la machine de l'adresse (`?asset=…`) avant
  // même qu'elle ait pu s'y trouver : un lien direct vers une machine retombait
  // toujours sur la première (défaut constaté le 2026-10-06).
  const [chargement, setChargement] = useState(true);
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  // Créer un client ou un site à la main : sans machine, ni l'un ni l'autre n'a de fiche à atteindre depuis la liste.
  const [creation, setCreation] = useState<'client' | 'site' | null>(null);
  const [annonce, setAnnonce] = useState<string | null>(null);

  const definir = useCallback(
    (cle: string, valeur: string | null, options?: { push?: boolean }) => {
      setParams(
        (precedent) => {
          const suivant = new URLSearchParams(precedent);
          if (valeur) suivant.set(cle, valeur);
          else suivant.delete(cle);
          return suivant;
        },
        { replace: !options?.push },
      );
    },
    [setParams],
  );

  // Débat de la recherche : l'URL ne se met à jour que 250 ms après la
  // dernière frappe, comme partout ailleurs dans l'application.
  useEffect(() => {
    const minuteur = setTimeout(() => {
      const propre = recherche.trim();
      if (propre !== q) definir('q', propre || null);
    }, 250);
    return () => clearTimeout(minuteur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recherche]);

  // Revenir à la première page quand un filtre change vraiment.
  useEffect(() => {
    setTake(TAILLE_PAGE);
  }, [q, client, site, nature, chip]);

  const chargerListe = useCallback(async () => {
    setChargement(true);
    try {
      const resultat = await api.assets({
        search: q || undefined,
        customerId: client ?? undefined,
        siteId: site ?? undefined,
        assetType: nature ?? undefined,
        chip: chip ?? undefined,
        take,
      });
      setItems([...resultat.items]);
      setTotal(resultat.total);
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Chargement impossible.');
    } finally {
      setChargement(false);
    }
  }, [q, client, site, nature, chip, take]);

  useEffect(() => {
    void chargerListe();
  }, [chargerListe]);

  const chargerFacettes = useCallback(async () => {
    try {
      setFacets(
        await api.assetFacets({
          customerId: client ?? undefined,
          siteId: site ?? undefined,
          search: q || undefined,
        }),
      );
    } catch {
      // Les statistiques sont un confort, pas une preuve : leur échec ne
      // bloque pas la consultation du parc lui-même.
    }
  }, [client, site, q]);

  useEffect(() => {
    void chargerFacettes();
  }, [chargerFacettes]);

  // Lot T4 : combien de machines créées sur le téléphone attendent la validation du bureau (lien visible si N > 0).
  const [aValider, setAValider] = useState(0);
  useEffect(() => {
    let annule = false;
    api
      .machinesAValider()
      .then((l) => {
        if (!annule) setAValider(l.total);
      })
      .catch(() => undefined);
    return () => {
      annule = true;
    };
  }, [items]);
  const texteAValider = lienMachinesAValider(aValider);

  // Point de vérification : jamais de fiche vide au premier chargement, et
  // jamais une fiche hors du filtre courant — changer de client ou de site
  // laissait affichée l'ancienne machine sélectionnée, même absente de la
  // nouvelle liste (défaut constaté au navigateur le 2026-09-29).
  useEffect(() => {
    if (chargement) return;
    if (asset && items.some((it) => it.id === asset)) return;
    definir('asset', items[0]?.id ?? null);
  }, [asset, items, chargement, definir]);

  const choisirClient = useCallback(
    (id: string | null) => {
      setParams(
        (precedent) => {
          const suivant = new URLSearchParams(precedent);
          if (id) suivant.set('client', id);
          else suivant.delete('client');
          suivant.delete('site');
          return suivant;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // `useCallback` indispensable ici : passée telle quelle à `FicheMachine`, une
  // référence neuve à chaque rendu ferait rejouer son `useEffect` de chargement
  // en boucle (constaté au navigateur — la fiche restait sur « Chargement… »
  // pendant que l'API recevait des centaines de requêtes par seconde).
  const remplacerLigne = useCallback(
    (m: Asset) => {
      setItems((prev) => prev.map((it) => (it.id === m.id ? m : it)));
      // Une correction peut faire basculer une nature, une propriété tierce ou
      // une fiche incomplète : les statistiques doivent suivre, pas seulement
      // la ligne de liste.
      void chargerFacettes();
    },
    [chargerFacettes],
  );

  const retirerLigne = useCallback(
    (id: string) => {
      setItems((prev) => prev.filter((it) => it.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      definir('asset', null);
      void chargerFacettes();
    },
    [definir, chargerFacettes],
  );

  return (
    <>
      <div className="entete-page">
        <div>
          <h1>Le Parc</h1>
          <p>
            {total} machine{total > 1 ? 's' : ''}
            {chargement ? ' · actualisation…' : ''}
          </p>
        </div>
        <div className="actions">
          {texteAValider && (
            <Link className="bouton-lien" to="/parc/a-valider">
              {texteAValider}
            </Link>
          )}
          <Link
            className="bouton-lien secondaire-lien"
            to={`/machines/etiquettes?${new URLSearchParams({
              ...(q ? { search: q } : {}),
              ...(site ? { site } : {}),
            }).toString()}`}
          >
            Imprimer les étiquettes
          </Link>
          <Link className="bouton-lien secondaire-lien" to="/import">
            Importer des tiers
          </Link>
          <Link className="bouton-lien secondaire-lien" to="/sites/import">
            Importer des sites
          </Link>
          <Link className="bouton-lien secondaire-lien" to="/machines/import">
            Importer des machines
          </Link>
          <button className="secondaire" onClick={() => setCreation('client')}>
            Nouveau client
          </button>
          <button className="secondaire" onClick={() => setCreation('site')}>
            Nouveau site
          </button>
          <button onClick={() => setFormulaireOuvert(true)}>Nouvelle machine</button>
        </div>
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}
      {annonce && <div className="message succes">{annonce}</div>}

      <div className="carte">
        <div className="carte-corps parc-filtres">
          <input
            type="text"
            placeholder="Rechercher : désignation, modèle, série, QR, site, ville…"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
          />
          <ComboRecherche
            placeholder="Client"
            viderLibelle="Tous les clients"
            valeur={client}
            onChange={choisirClient}
            options={(facets?.customers ?? []).map((c) => ({
              id: c.id,
              label: c.legalName,
              count: c.activeAssetCount,
            }))}
          />
          <ChipsSite sites={facets?.sites ?? []} valeur={site} onChange={(id) => definir('site', id)} />
        </div>
      </div>

      <StatsNature
        byNature={facets?.byNature ?? []}
        valeur={nature}
        onChange={(v) => definir('nature', v)}
      />

      <ChipsSecondaires
        thirdPartyOwnedCount={facets?.thirdPartyOwnedCount ?? 0}
        espCount={facets?.espCount ?? 0}
        incompleteCount={facets?.incompleteCount ?? 0}
        valeur={chip}
        onChange={(v) => definir('chip', v)}
      />

      <div className="parc-mise-en-page">
        <div className="carte parc-liste">
          {items.length === 0 ? (
            <div className="vide">
              {chargement ? 'Chargement…' : 'Aucune machine ne correspond à ces filtres.'}
            </div>
          ) : (
            <>
              <ul className="parc-lignes">
                {items.map((m) => {
                  const etat = STATUT_MACHINE[m.status];
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        className={`parc-ligne${asset === m.id ? ' actif' : ''}`}
                        onClick={() => definir('asset', m.id, { push: true })}
                      >
                        <div className="parc-ligne-badges">
                          <span className="badge">{m.assetType}</span>
                          <span className="badge badge-propriete">
                            {m.ownerName ? `${m.ownerName} – Location` : 'Client'}
                          </span>
                        </div>
                        <div className="parc-ligne-titre">
                          <strong>{m.label ?? m.model ?? 'Sans désignation'}</strong>
                          {m.reviewStatus === 'TO_VALIDATE' && <span className="etat attente">à valider</span>}
                          <span className={`etat ${etat.classe}`}>{etat.texte}</span>
                        </div>
                        <div className="aide">
                          {[m.customerName, m.siteName].filter(Boolean).join(' · ') || 'Sans site'}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {items.length < total && (
                <div className="parc-charger-plus">
                  <button
                    className="secondaire"
                    disabled={chargement}
                    onClick={() => setTake((t) => t + TAILLE_PAGE)}
                  >
                    Charger plus ({total - items.length} restantes)
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="parc-fiche">
          {asset ? (
            <FicheMachine
              key={asset}
              assetId={asset}
              onUpdated={remplacerLigne}
              onArchived={() => retirerLigne(asset)}
            />
          ) : (
            <div className="vide">Sélectionnez une machine.</div>
          )}
        </div>
      </div>

      {creation === 'client' && (
        <FormulaireTiers
          tiers={null}
          rolesInitiaux={['CUSTOMER']}
          titreCreation="Nouveau client"
          onFermer={() => setCreation(null)}
          onEnregistre={(cree) => {
            setAnnonce(`Client « ${cree.legalName} » créé. Il n’apparaît dans la liste des clients qu’avec sa première machine : créez-lui un site (« Nouveau site »), puis une machine.`);
            void chargerFacettes();
          }}
        />
      )}

      {creation === 'site' && (
        <FormulaireSite
          site={null}
          clientImpose={client ?? undefined}
          onFermer={() => setCreation(null)}
          onEnregistre={(cree) => {
            setAnnonce(`Site « ${cree.name} » créé chez « ${cree.customerName} ». Vous pouvez maintenant lui ajouter une machine (« Nouvelle machine »).`);
            void chargerFacettes();
          }}
        />
      )}

      {formulaireOuvert && (
        <FormulaireMachine
          machine={null}
          siteImpose={site ?? undefined}
          onFermer={() => setFormulaireOuvert(false)}
          onEnregistre={(nouvelle) => {
            void chargerListe();
            void chargerFacettes();
            definir('asset', nouvelle.id, { push: true });
          }}
        />
      )}
    </>
  );
}
